import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, ArrowLeft, Plus, MapPin, Cpu, Building2, Phone, CheckCircle2, XCircle, PhoneOff, PhoneMissed, AlertTriangle } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import MetaCard from "@/components/MetaCard";
import StatusBadge from "@/components/StatusBadge";
import CallLogDialog from "@/components/CallLogDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatDate } from "@/lib/format";
import { readBack, useWithBack, segmentBackOverride } from "@/lib/nav";

const OUTCOME_META = {
  booked:     { label: "Booked",     tone: "success", icon: CheckCircle2 },
  declined:   { label: "Declined",   tone: "warning", icon: XCircle },
  no_answer:  { label: "No answer",  tone: "muted",   icon: PhoneMissed },
  bad_number: { label: "Bad number", tone: "danger",  icon: PhoneOff },
};

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [sites, setSites] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callLogOpen, setCallLogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const backCtx = readBack(searchParams);
  const withBackHref = useWithBack();
  const activeTab = searchParams.get("tab") || "details";
  const setTab = (v) => {
    // Radix TabsTrigger fires onValueChange twice per click (mousedown +
    // focus in activationMode='automatic'). React state (`activeTab`) is
    // stale for the second call and can't be used to short-circuit. Compare
    // against `window.location.search` — the first push has already updated
    // it synchronously by the second invocation.
    const current = new URLSearchParams(window.location.search).get("tab") || "details";
    if (v === current) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: false });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: s }, { data: m }] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/sites`),
        api.get(`/machines`, { params: { customer_id: id } }),
      ]);
      setCustomer(c);
      setSites(s);
      setMachines(m.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/customers", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !customer) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="customer-detail-page">
      <Breadcrumbs
        items={[
          { label: "Customers", to: segmentBackOverride("/customers", backCtx?.to) },
          { label: customer.name },
        ]}
      />
      <PageHeader
        testId="customer-detail-header"
        title={customer.name}
        subtitle={
          <span className="flex items-center gap-2">
            {customer.active
              ? <StatusBadge tone="success">Active</StatusBadge>
              : <StatusBadge tone="muted">Inactive</StatusBadge>}
            {customer.on_demand_service && <StatusBadge tone="warning">On-demand</StatusBadge>}
            <span>{customer.site_count} sites · {customer.machine_count} machines</span>
          </span>
        }
        actions={
          <>
            <Button asChild variant="outline" size="sm" data-testid="customer-detail-back">
              <Link to={backCtx?.to || "/customers"}><ArrowLeft className="h-4 w-4" /> Back</Link>
            </Button>
            {hasPermission("call_log.write") && (
              <Button size="sm" variant="outline" onClick={() => setCallLogOpen(true)} data-testid="customer-detail-log-call">
                <Phone className="h-4 w-4" /> Log call
              </Button>
            )}
            {hasPermission("customer.write") && (
              <Button asChild size="sm" data-testid="customer-detail-edit">
                <Link to={`/customers/${customer.id}/edit`}><Pencil className="h-4 w-4" /> Edit</Link>
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Tabs value={activeTab} onValueChange={setTab}>
            <TabsList className="rounded-lg bg-muted p-1">
              <TabsTrigger value="details" data-testid="customer-tab-details">Details</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="customer-tab-contacts">Contacts ({(customer.contacts || []).length})</TabsTrigger>
              <TabsTrigger value="sites" data-testid="customer-tab-sites">Sites ({sites.length})</TabsTrigger>
              <TabsTrigger value="machines" data-testid="customer-tab-machines">Machines ({machines.length})</TabsTrigger>
              <TabsTrigger value="call-log" data-testid="customer-tab-call-log">Call Log ({(customer.call_log || []).length})</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-4 space-y-6" data-testid="tab-content-details">
              <Card className="rounded-md">
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" /> Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <Field label="Contacts" value={String((customer.contacts || []).length)} />
                  <Field label="Report recipients" value={String((customer.contacts || []).filter((c) => c.is_report_recipient).length)} />
                  <Field label="Email reports" value={customer.email_reports_default ? "Yes" : "No"} />
                  <Field label="On-demand" value={customer.on_demand_service ? "Yes" : "No"} />
                </CardContent>
              </Card>

              <Card className="rounded-md">
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Billing address</CardTitle>
                </CardHeader>
                <CardContent className="text-sm leading-relaxed">
                  <div>{customer.billing_address?.street || "—"}</div>
                  <div>{[customer.billing_address?.city, customer.billing_address?.postcode].filter(Boolean).join(", ") || "—"}</div>
                  <div>{customer.billing_address?.country}</div>
                </CardContent>
              </Card>

              {customer.notes && (
                <Card className="rounded-md">
                  <CardHeader><CardTitle className="text-sm font-medium">Notes</CardTitle></CardHeader>
                  <CardContent className="text-sm leading-relaxed whitespace-pre-wrap">{customer.notes}</CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="contacts" className="mt-4 space-y-4" data-testid="tab-content-contacts">
              {customer.email_reports_default && (customer.contacts || []).length > 0 &&
               !(customer.contacts || []).some((c) => c.is_report_recipient) && (
                <div
                  data-testid="customer-contacts-no-recipient-warning"
                  className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-foreground"
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-warning" />
                  <div>
                    <div className="font-medium">Reports are enabled but no recipients are flagged.</div>
                    <div className="text-muted-foreground">
                      This customer has email reports enabled but no contacts are currently set to receive them.
                      Reports will not be sent until at least one contact is flagged.
                    </div>
                  </div>
                </div>
              )}
              <Card className="rounded-md">
                <CardContent className="p-0">
                  {(customer.contacts || []).length === 0 ? (
                    <div className="p-6 text-xs text-muted-foreground italic" data-testid="customer-contacts-empty">
                      No contacts yet. Use the Edit button to add contacts.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-xs font-bold uppercase tracking-wider text-muted-foreground">
                          <th className="text-left py-2 px-3">Name</th>
                          <th className="text-left py-2 px-3">Role</th>
                          <th className="text-left py-2 px-3">Email</th>
                          <th className="text-left py-2 px-3">Phone</th>
                          <th className="text-left py-2 px-3">Receives reports</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(customer.contacts || []).map((c) => (
                          <tr key={c.id} className="border-b" data-testid={`customer-contact-row-${c.id}`}>
                            <td className="py-2 px-3 font-medium">{c.name}</td>
                            <td className="py-2 px-3"><StatusBadge tone="info">{c.role}</StatusBadge></td>
                            <td className="py-2 px-3 font-mono text-xs">{c.email || "—"}</td>
                            <td className="py-2 px-3 font-mono text-xs">{c.phone || "—"}</td>
                            <td className="py-2 px-3">
                              {c.is_report_recipient
                                ? <StatusBadge tone="warning">Yes</StatusBadge>
                                : <StatusBadge tone="muted">No</StatusBadge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="sites" className="mt-4" data-testid="tab-content-sites">
              <Card className="rounded-md">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Sites</CardTitle>
                  {hasPermission("site.write") && (
                    <Button asChild size="sm" variant="outline" data-testid="customer-detail-new-site">
                      <Link to={`/sites/new?customer_id=${customer.id}&back=${encodeURIComponent(`/customers/${customer.id}?tab=sites`)}`}><Plus className="h-3.5 w-3.5" /> Add site</Link>
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  {sites.length === 0 ? (
                    <div className="py-8 text-center text-xs text-muted-foreground" data-testid="customer-sites-empty">No sites yet.</div>
                  ) : (
                    <ul className="divide-y divide-border">
                      {sites.map((s) => (
                        <li key={s.id} className="p-3 flex items-center justify-between hover:bg-muted/30" data-testid={`customer-site-${s.id}`}>
                          <div>
                            <Link to={withBackHref(`/sites/${s.id}`)} className="text-sm font-medium hover:text-primary" data-testid={`customer-site-link-${s.id}`}>{s.name}</Link>
                            <div className="text-xs text-muted-foreground">{s.address?.city} · {s.on_site_contact_name || "no contact"}</div>
                          </div>
                          {s.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="machines" className="mt-4" data-testid="tab-content-machines">
              <Card className="rounded-md">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium flex items-center gap-2"><Cpu className="h-4 w-4 text-primary" /> Machines</CardTitle>
                  {hasPermission("machine.write") && (
                    <Button asChild size="sm" variant="outline" data-testid="customer-machines-add-button">
                      <Link to={`/machines/new?customer_id=${customer.id}&back=${encodeURIComponent(`/customers/${customer.id}?tab=machines`)}`}><Plus className="h-3.5 w-3.5" /> Add machine</Link>
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  {machines.length === 0 ? (
                    <div className="py-8 text-center text-xs text-muted-foreground">No machines yet.</div>
                  ) : (
                    <ul className="divide-y divide-border">
                      {machines.map((m) => (
                        <li key={m.id} className="p-3 flex items-center justify-between hover:bg-muted/30" data-testid={`customer-machine-${m.id}`}>
                          <div>
                            <Link to={withBackHref(`/machines/${m.id}`)} className="text-sm font-medium hover:text-primary" data-testid={`customer-machine-link-${m.id}`}>{m.make} {m.model}</Link>
                            <div className="text-xs text-muted-foreground">Serial {m.serial}</div>
                          </div>
                          {m.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="call-log" className="mt-4" data-testid="tab-content-call-log">
              <Card className="rounded-md">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> Call log</CardTitle>
                  {hasPermission("call_log.write") && (
                    <Button size="sm" variant="outline" onClick={() => setCallLogOpen(true)} data-testid="customer-call-log-add">
                      <Plus className="h-3.5 w-3.5" /> New entry
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  {(customer.call_log || []).length === 0 ? (
                    <div className="py-8 text-center text-xs text-muted-foreground" data-testid="customer-call-log-empty">
                      No call outcomes logged yet.
                    </div>
                  ) : (
                    <ul className="divide-y divide-border">
                      {[...(customer.call_log || [])].reverse().map((entry) => {
                        const meta = OUTCOME_META[entry.outcome] || { label: entry.outcome, tone: "muted", icon: Phone };
                        const Icon = meta.icon;
                        return (
                          <li key={entry.id || entry.at} className="p-3 flex items-start gap-3" data-testid={`customer-call-log-entry-${entry.id || entry.at}`}>
                            <div className="mt-0.5"><Icon className="h-4 w-4 text-muted-foreground" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(entry.at)}{entry.by_username ? ` · by ${entry.by_username}` : ""}
                                </span>
                              </div>
                              {entry.note && (
                                <div className="text-sm mt-1 whitespace-pre-wrap leading-relaxed">{entry.note}</div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <MetaCard
            title="Metadata"
            fields={[
              { label: "Status", value: customer.active ? "Active" : "Inactive" },
              { label: "On-demand", value: customer.on_demand_service ? "Yes" : "No", testId: "customer-meta-on-demand" },
              { label: "Sites", value: customer.site_count, testId: "customer-meta-site-count" },
              { label: "Machines", value: customer.machine_count, testId: "customer-meta-machine-count" },
              { label: "Created", value: formatDate(customer.created_at) },
              { label: "Updated", value: formatDate(customer.updated_at) },
            ]}
          />
        </div>
      </div>

      <CallLogDialog
        open={callLogOpen}
        onOpenChange={setCallLogOpen}
        customer={customer}
        onLogged={() => load()}
      />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-0.5">{value || "—"}</div>
    </div>
  );
}
